import { FormattedMessage } from 'react-intl';
import { clsx } from 'clsx';

import Divider from '../common/Divider';
import { Button } from '../common/Button';

import { useLocale } from '../lang/LocalizationProvider';
import styles from './PlanCard.module.scss';

import { type Plan } from '@/utils/mapProductObject';

interface PlanCardProps {
  plan: Plan;
  onButtonClick: () => void;
}

const PlanCard = ({ plan, onButtonClick }: PlanCardProps) => {
  const { selectedLocalization } = useLocale();

  const isWeeklyPlan = plan.sku.includes('weekly');

  return (
    <div className={clsx(styles.card, (plan.showBestValueLabel || plan.showSpecialOfferLabel) && styles.bestCard)}>
      {plan.showSpecialOfferLabel && (
        <span className={styles.bestValueLabel}>
          <FormattedMessage
            id="plansScreen.plan.specialOfferLabel"
            defaultMessage="Special offer"
            description="Special offer label on one of the Plan cards"
          />
        </span>
      )}
      {!plan.showSpecialOfferLabel && plan.showBestValueLabel && (
        <span className={styles.bestValueLabel}>
          <FormattedMessage
            id="plansScreen.plan.bestChoiceLabel"
            defaultMessage="Best choice!"
            description="Best value label on one of the Plan cards"
          />
        </span>
      )}

      <h3 className={styles.title}>{plan.name[selectedLocalization]}</h3>

      <Divider spacing={16} />

      <span className={styles.price}>
        {plan.noTaxInitialMonthlyCost && plan.noTaxInitialMonthlyCost !== plan.noTaxMonthlyCost && (
          <span className={clsx(styles.initialPrice, styles.initialPriceMonth)}>{plan.noTaxInitialMonthlyCost}</span>
        )}

        {isWeeklyPlan ? (
          <FormattedMessage
            id="plansScreen.plan.weeklyCost"
            defaultMessage="{weeklyCost} / week"
            values={{ weeklyCost: plan.noTaxTotal }}
            description="Cost of plan per week"
          />
        ) : (
          <FormattedMessage
            id="plansScreen.plan.monthlyCost"
            defaultMessage="{monthlyCost} / month"
            values={{ monthlyCost: plan.noTaxMonthlyCost }}
            description="Cost of plan per month"
          />
        )}
      </span>
      <span className={clsx(styles.priceDisclaimer, plan.hasTrial && styles.priceDisclaimerWithTrial)}>
        {plan.noTaxTotal !== plan.noTaxMonthlyCost && !isWeeklyPlan && (
          <>
            {plan.noTaxInitialTotal && plan.noTaxInitialTotal !== plan.noTaxTotal && (
              <span className={styles.initialPrice}>{plan.noTaxInitialTotal}</span>
            )}

            <FormattedMessage
              id="plansScreen.plan.totalPriceDescription"
              description="Price description line on Plan card; specifies total cost, billing period and presence of trial"
              defaultMessage="{totalPrice} billed every {renewPeriod} {hasTrial, select,
            true {{lineBreak} after trial}
            other {}
          }"
              values={{
                totalPrice: <b>{plan.noTaxTotal}</b>,
                renewPeriod: plan.name[selectedLocalization].toLocaleLowerCase(),
                hasTrial: plan.hasTrial,
                lineBreak: <br />,
              }}
            />
          </>
        )}
      </span>

      <Button kind="primary" className={styles.cta} onClick={onButtonClick}>
        {plan.hasTrial ? (
          <FormattedMessage
            id="plansScreen.plan.selectTrialPlanButton"
            description="Text of a button that selects a plan with trial and forwards user to payment screen"
            defaultMessage="Start {trialPeriodLengthDays, plural,
              one {free 24-hours}
              other {# free days}
            }"
            values={{
              trialPeriodLengthDays: plan.trialDays,
            }}
          />
        ) : (
          <FormattedMessage
            id="plansScreen.plan.selectPlanButton"
            defaultMessage="Get started"
            description="Text of a button that selects the plan and forwards user to payment screen"
          />
        )}
      </Button>
    </div>
  );
};

export default PlanCard;
